import React, { useEffect, useState } from "react";
import {
  Box,
  Drawer,
  Typography,
  TextField,
  Button,
  FormControl,
  Select,
  MenuItem,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
  FormHelperText,
  Grid,
  Avatar,
  Tooltip,
  CircularProgress
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import { useDispatch } from "react-redux";
import {
  getApplicants,
  getApplicationStatusHistory,
  getInterviewers,
  getInterviewRounds,
  getInterviwerEvents,
  getSingleApplicantById,
  interviewBusyShedule,
  postInterviewRound,
  updateInterviewRound,
} from "../../../Redux/Reducers/JobReducer";
import { useSelector } from "react-redux";
import { getGoogleLocalPlaces } from "../../../Redux/helpers/JobsApi";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import {
  DatePicker,
  DateTimePicker,
  LocalizationProvider,
  PickersDay,
} from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { monthFirstDate, monthLastDate } from "../../Utilities/DateUtils";
import { v4 as uuidv4 } from "uuid";
import TimePicker from "Components/Utilities/TimePicker";
import { useQuantityContext } from "Components/QuantityContext";

const BusySlotModal = ({ open, onClose, slots, date, onScheduleAnyway }) => {
  // Helper function to merge overlapping time slots for the same interviewer
  const mergeOverlappingSlots = (slots) => {
    // Group slots by interviewer
    const interviewerSlots = slots.reduce((acc, slot) => {
      const email = slot.interviewer.email;
      if (!acc[email]) {
        acc[email] = {
          interviewer: slot.interviewer,
          slots: [],
        };
      }
      acc[email].slots.push({ start: dayjs(slot.start), end: dayjs(slot.end) });
      return acc;
    }, {});

    // Merge overlapping slots for each interviewer
    const mergedSlots = Object.values(interviewerSlots).map(
      ({ interviewer, slots }) => {
        // Sort slots by start time
        const sortedSlots = slots.sort(
          (a, b) => a.start.valueOf() - b.start.valueOf()
        );

        // Merge overlapping slots
        const mergedTimes = sortedSlots.reduce((merged, current) => {
          if (
            !merged.length ||
            merged[merged.length - 1].end.isBefore(current.start)
          ) {
            merged.push(current);
          } else {
            // Use the later end time between current slot and last merged slot
            const lastMerged = merged[merged.length - 1];
            lastMerged.end = lastMerged.end.isAfter(current.end)
              ? lastMerged.end
              : current.end;
          }
          return merged;
        }, []);

        return {
          interviewer,
          timeSlots: mergedTimes,
        };
      }
    );

    return mergedSlots;
  };

  const consolidatedSlots = mergeOverlappingSlots(slots);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Busy Schedule for {dayjs(date).format("MMMM D, YYYY")} at{" "}
        {dayjs(date).format("hh:mm A")}
      </DialogTitle>
      <DialogContent>
        <Typography color="error" gutterBottom>
          The following interviewers are busy during this time slot:
        </Typography>
        {consolidatedSlots.map((interviewerData, index) => (
          <Box
            key={index}
            sx={{ mb: 2, p: 2, bgcolor: "#f5f5f5", borderRadius: 1 }}
          >
            <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
              <Avatar
                src={interviewerData.interviewer.avatar}
                alt={interviewerData.interviewer.name}
                sx={{ mr: 1, width: 32, height: 32 }}
              />
              <Box>
                <Typography variant="subtitle2">
                  {interviewerData.interviewer.name}
                </Typography>
                <Typography variant="caption">
                  {interviewerData.interviewer.email}
                </Typography>
              </Box>
            </Box>
            {interviewerData.timeSlots.map((timeSlot, idx) => (
              <Typography key={idx} variant="body2">
                Busy from {timeSlot.start.format("h:mm A")} to{" "}
                {timeSlot.end.format("h:mm A")}
              </Typography>
            ))}
          </Box>
        ))}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Choose Different Time</Button>
        <Button onClick={onScheduleAnyway} variant="contained" color="warning">
          Schedule Anyway
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const InterviewScheduleSlider = ({
  open,
  onClose,
  candidateId,
  existingRound,
  isEditMode,
  applicant,
}) => {
  // console.log("existingRound", existingRound, candidateId);
  const dispatch = useDispatch();
  const [errors, setErrors] = useState({});
  const now = dayjs();
  const maxDate = now.add(30, "day");
  const { openSnackbar } = useQuantityContext();
  const [interviewData, setInterviewData] = useState({
    title: "",
    type: "",
    startTime: "",
    endTime: "",
    interview_location: "",
    description: "",
    deadline: "",
    attachment: null,
    interviewers_list: [],
    round_code: "", // Add this field
    assignment_link: "",
    meet_link: "",
  });

  const InterviewRoundsRes = useSelector(
    (state) => state?.jobs?.interviewRounds
  );


  const InterviwerEvents = useSelector(
    (state) => state?.jobs?.InterviwerEvents
  );



  const isTimeInPast = (dateTime) => {
    return dayjs(dateTime).isBefore(dayjs());
  };



  const filteredRounds = InterviewRoundsRes?.filter(
    (round) => round.round_code === existingRound
  );

  const [isInterviewerModalOpen, setInterviewerModalOpen] = useState(false);
  const [newInterviewer, setNewInterviewer] = useState("");
  const interviewers =
    useSelector((state) => state?.jobs?.interviewers?.interviewers) || [];

  const [selectedInterviewers, setSelectedInterviewers] = useState([]);
  const [currentMonth, setCurrentMonth] = useState(dayjs());
  const [busyDates, setBusyDates] = useState([]);
  const maxInterviewers = 3;
  const [locations, setLocations] = useState([]);
  const [showBusySlotModal, setShowBusySlotModal] = useState(false);
  const [selectedDateBusySlots, setSelectedDateBusySlots] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const selectedCompany =
    useSelector((state) => state?.jobs?.selectedCompany) || {};
  const interviewersbusyslots =
    useSelector((state) => state?.jobs?.interviewersbusyslots) || {};
  const parsedStart = interviewData?.startTime
    ? dayjs(interviewData.startTime)
    : null;

  // console.log("interviewersbusyslots", interviewersbusyslots);
  // console.log("showBusySlotModal", showBusySlotModal);
  // console.log("applicant--Data", applicant)

  useEffect(() => {
    const params = {
      application_id: candidateId
    }
    dispatch(getInterviwerEvents());
    dispatch(getInterviewers(params));
  }, []);

  useEffect(() => {
    if (isEditMode && existingRound) {
      const existingData = filteredRounds?.[0]; // Extract the first round object
      const defaultTime = dayjs().add(1, 'day').hour(10).minute(0);
      if (existingData) {
        setInterviewData({
          title: existingData?.name || "", // Correct field name from your data
          type: existingData?.type || "",
          // startTime:
          //   existingData?.interview_schedule?.start_time?.slice(0, 16) || "",
          startTime:
            defaultTime.toISOString() || "",
          // endTime:
          //   existingData?.interview_schedule?.end_time?.slice(0, 16) || "",
          endTime: defaultTime.add(1, 'hour').toISOString() || "",
          interview_location: existingData?.interview_location || "",
          description: existingData?.description || "",
          deadline: existingData?.deadline?.slice(0, 16) || "",
          attachment: existingData?.attachment || null, // Match API property name
          interviewers_list:
            existingData?.interviewers_list?.map((i) => i?.email) || [], // Extract only emails
          round_code: existingData?.round_code || "",
          assignment_link: existingData?.assignment_link || "",
          meet_link: existingData?.interview_schedule?.meet_link,
        });
      }
    } else {
      setInterviewData({
        title: "",
        type: "",
        startTime: "",
        endTime: "",
        interview_location: "",
        description: "",
        deadline: "",
        attachment: null,
        interviewers_list: [],
        round_code: "",
        assignment_link: "",
        meet_link: "",
      });
    }
  }, [isEditMode, existingRound]);



  useEffect(() => {
    if (interviewData?.interviewers_list?.length > 0) {
      const params = {
        companyName: selectedCompany?.company_name,
        emails: interviewData?.interviewers_list.join(","),
        timeMin: dayjs(currentMonth).startOf("month").toISOString(),
        timeMax: dayjs(currentMonth).endOf("month").toISOString(),
      };

      // console.log("paramsBusySlots", params);

      dispatch(interviewBusyShedule(params))
        .then((response) => {
          console.log("Busy schedule response:", response);
        })
        .catch((error) => {
          console.error("Error fetching busy schedule:", error);
        });
    }
  }, [
    interviewData.interviewers_list,
    currentMonth,
    selectedCompany?.company_name,
  ]);

  const isBusyDate = (date) => {
    if (!interviewersbusyslots || !date) return false;
    const dateStr = dayjs(date).format("YYYY-MM-DD");
    return Object.keys(interviewersbusyslots).some((email) =>
      interviewersbusyslots[email].some(
        (slot) => dayjs(slot.start).format("YYYY-MM-DD") === dateStr
      )
    );
  };

  const getBusySlots = (date) => {
    if (!interviewersbusyslots || !date) return [];

    const dateStr = dayjs(date).format("YYYY-MM-DD");
    const busySlots = [];

    Object.entries(interviewersbusyslots).forEach(([email, slots]) => {
      const interviewer = interviewers.find((i) => i.email === email);
      slots.forEach((slot) => {
        const slotDate = dayjs(slot.start).format("YYYY-MM-DD");
        if (slotDate === dateStr) {
          busySlots.push({
            interviewer: {
              name: `${interviewer?.first_name} ${interviewer?.last_name}`,
              email,
              avatar: interviewer?.profile_url,
            },
            start: dayjs(slot.start),
            end: dayjs(slot.end),
          });
        }
      });
    });

    // Sort slots by start time
    return busySlots.sort((a, b) => a.start.valueOf() - b.start.valueOf());
  };

  // const validateForm = () => {
  //   const newErrors = {};

  //   if (!interviewData?.title) newErrors.title = "Round title is required";
  //   if (!interviewData?.type) newErrors.type = "Interview type is required";
  //   if (!interviewData?.description)
  //     newErrors.description = "Description is required";

  //   if (interviewData?.type === "F2F" || interviewData?.type === "Remote") {
  //     if (!interviewData?.startTime)
  //       newErrors.startTime = "Start time is required";
  //     if (!interviewData?.endTime) newErrors.endTime = "End time is required";
  //     // Only validate interviewers for F2F and Remote
  //     if (
  //       !interviewData?.interviewers_list ||
  //       interviewData.interviewers_list?.length === 0
  //     ) {
  //       newErrors.interviewers = "At least one interviewer is required";
  //     }
  //   }

  //   if (interviewData?.type === "F2F" && !interviewData?.interview_location) {
  //     newErrors.interview_location = "Location is required";
  //   }

  //   if (interviewData?.type === "Remote" && !interviewData?.meet_link) {
  //     newErrors.meet_link = "Meetlink is required";
  //   }

  //   // if (interviewData?.type === 'Assignment' && !interviewData?.deadline) {
  //   //   newErrors.deadline = 'Submission deadline is required';
  //   // }

  //   // Enhanced deadline validation
  //   if (interviewData?.type === "Assignment") {
  //     if (!interviewData?.deadline) {
  //       newErrors.deadline = "Submission deadline is required";
  //     } else {
  //       const deadlineDate = dayjs(interviewData.deadline);
  //       const now = dayjs();
  //       const maxDeadline = now.add(30, "day");

  //       if (deadlineDate.isBefore(now)) {
  //         newErrors.deadline = "Deadline cannot be in the past";
  //       } else if (deadlineDate.isAfter(maxDeadline)) {
  //         newErrors.deadline = "Deadline cannot be more than 30 days from now";
  //       }
  //     }
  //   }

  //   setErrors(newErrors);
  //   return Object.keys(newErrors).length === 0;
  // };
  const validateForm = () => {
    const newErrors = {};

    if (!interviewData?.title) newErrors.title = "Round title is required";
    if (!interviewData?.type) newErrors.type = "Interview type is required";
    if (!interviewData?.description)
      newErrors.description = "Description is required";

    if (interviewData?.type === "F2F" || interviewData?.type === "Remote") {
      if (!interviewData?.startTime)
        newErrors.startTime = "Start time is required";
      if (!interviewData?.endTime) newErrors.endTime = "End time is required";

      // // Validate end time is after start time
      // if (interviewData?.startTime && interviewData?.endTime) {
      //   const start = dayjs(interviewData.startTime);
      //   const end = dayjs(interviewData.endTime);
      //   if (!end.isAfter(start)) {
      //     newErrors.endTime = "End time must be greater than start time";
      //   }
      // }

      // Only validate interviewers for F2F and Remote
      if (
        !interviewData?.interviewers_list ||
        interviewData.interviewers_list?.length === 0
      ) {
        newErrors.interviewers = "At least one interviewer is required";
      }
    }

    if (interviewData?.type === "F2F" && !interviewData?.interview_location) {
      newErrors.interview_location = "Location is required";
    }

    if (interviewData?.type === "Remote" && !interviewData?.meet_link) {
      newErrors.meet_link = "Meetlink is required";
    }

    // Enhanced deadline validation
    if (interviewData?.type === "Assignment") {
      if (!interviewData?.deadline) {
        newErrors.deadline = "Submission deadline is required";
      } else {
        const deadlineDate = dayjs(interviewData.deadline);
        const now = dayjs();
        const maxDeadline = now.add(30, "day");

        if (deadlineDate.isBefore(now)) {
          newErrors.deadline = "Deadline cannot be in the past";
        } else if (deadlineDate.isAfter(maxDeadline)) {
          newErrors.deadline = "Deadline cannot be more than 30 days from now";
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateTimes = (startDate, endDate) => {
    const newErrors = {};

    if (startDate) {
      if (startDate.isBefore(dayjs())) {
        newErrors.startTime = "Start time cannot be in the past";
      } else {
        newErrors.startTime = "";
      }
    } else {
      newErrors.startTime = "";
    }

    if (endDate) {
      if (endDate.isBefore(dayjs())) {
        newErrors.endTime = "End time cannot be in the past";
      } else {
        newErrors.endTime = "";
      }
    } else {
      newErrors.endTime = "";
    }

    if (startDate && endDate) {
      if (!endDate.isAfter(startDate)) {
        newErrors.endTime = "End time must be greater than start time";
      }
    }

    setErrors((prev) => ({ ...prev, ...newErrors }));
  };


  const handleChange = (e) => {
    const { name, value } = e.target;
    setInterviewData((prev) => ({
      ...prev,
      [name]: value,
      attachment: name === "assignment_link" ? null : prev.attachment,
    })); // Clear file if link is entered
  };

  const handleFileUpload = (e) => {
    setInterviewData((prev) => {
      console.log("PreviousData", prev);
      return { ...prev, attachment: e.target.files[0], assignment_link: "" };
    });
    // Clear link if file is uploaded
  };

  const handleAddInterviewer = () => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (newInterviewer.trim() && emailRegex.test(newInterviewer)) {
      // Fixed: Actually add the new interviewer to the array
      setInterviewData((prev) => ({
        ...prev,
        interviewers_list: [...prev.interviewers_list, newInterviewer.trim()],
      }));
      setNewInterviewer("");
      setInterviewerModalOpen(false);
    } else {
      setErrors((prev) => ({
        ...prev,
        newInterviewer: "Please enter a valid email address",
      }));
    }
  };

  const validateAssignment = () => {
    if (
      interviewData?.type === "Assignment" &&
      !interviewData?.attachment &&
      !interviewData?.assignment_link
    ) {
      return "Either an assignment file or a link is required.";
    }
    return "";
  };

  const assignmentError = validateAssignment();

  const handleSubmit = async () => {
    if (!validateForm()) return;
    setIsSubmitting(true);

    const interviewersArray = interviewData?.interviewers_list
      .map((email) => {
        const user = interviewers?.find((intv) => intv.email === email);
        if (!user) {
          console.error(`Interviewer not found: ${email}`);
          return null;
        }
        return {
          email: user.email,
          name: `${user.first_name} ${user.last_name}`,
          user_ref_id: user.reference_id,
          ...(user.profile_url && { profile_pic: user.profile_url }),
        };
      })
      .filter(Boolean);

    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const round_code = interviewData?.round_code || uuidv4();

    let payload;

    if (interviewData?.type === "Assignment") {
      // For Assignment interviews
      const payloadData = {
        application_id: candidateId,
        round_code: round_code,
        timeZone: timeZone,
        title: interviewData.title,
        description: interviewData.description,
        type: interviewData.type,
        deadline: new Date(interviewData.deadline).toISOString(),
        assignment_link: interviewData.assignment_link,
        attachment: interviewData.attachment,
      };

      payload = {
        round_code: round_code,
        formData: payloadData,
      };
    } else {
      // For F2F and Remote interviews
      const payloadData = {
        application_id: candidateId,
        round_code: round_code,
        timeZone: timeZone,
        title: interviewData.title,
        description: interviewData.description,
        type: interviewData.type,
        startTime: new Date(interviewData.startTime).toISOString(),
        endTime: new Date(interviewData.endTime).toISOString(),
        interviewers_list: interviewersArray,
        ...(interviewData.type === "Remote" && {
          meet_link: interviewData.meet_link,
        }),
        ...(interviewData.type === "F2F" && {
          interview_location: interviewData.interview_location,
        }),
      };

      payload = {
        round_code: round_code,
        formData: payloadData,
      };
    }

    try {
      const action = existingRound ? updateInterviewRound : postInterviewRound;
      const response = await dispatch(action(payload));

      if (response.payload?.status === "SUCCESS") {
        onClose();
        dispatch(getInterviewRounds(candidateId));
        dispatch(getSingleApplicantById(candidateId));
        dispatch(getApplicants({ job_id: applicant?.job_id }));
        dispatch(getApplicationStatusHistory({ id: candidateId }));
      }
      else{
        openSnackbar(response.payload, "error", "3000");
      }
    } catch (error) {
      console.error("Error saving interview round:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInterviewerSelect = (interviewer) => {
    if (selectedInterviewers?.find((i) => i.email === interviewer.email)) {
      setSelectedInterviewers((prev) =>
        prev.filter((i) => i.email !== interviewer.email)
      );
    } else if (selectedInterviewers?.length < maxInterviewers) {
      setSelectedInterviewers((prev) => [...prev, interviewer]);
    }
  };

  const handleAddSelectedInterviewers = () => {
    // Add selected emails to interviewData
    const selectedEmails = selectedInterviewers?.map((i) => i.email);
    setInterviewData((prev) => ({
      ...prev,
      interviewers_list: [...prev.interviewers_list, ...selectedEmails],
    }));

    // Clear selection
    setSelectedInterviewers([]);
    setInterviewerModalOpen(false);
  };

  const removeInterviewer = (emailToRemove) => {
    setInterviewData((prev) => ({
      ...prev,
      interviewers_list: prev?.interviewers_list?.filter(
        (email) => email !== emailToRemove
      ),
    }));
  };

  const getInterviewerDetails = (email) => {
    return (
      interviewers?.find((interviewer) => interviewer?.email === email) || null
    );
  };

  const onChangeLocation = async (input) => {
    try {
      // Input validation
      if (!input || typeof input !== "string") {
        setLocations([]);
        return;
      }

      // Only fetch if input is longer than 2 characters
      if (input.length > 2) {
        // Show loading state
        setLocations([]);

        const response = await getGoogleLocalPlaces(input);

        // Handle different response formats
        if (Array.isArray(response)) {
          // Handle simple string array response
          setLocations(
            response.map((place) => ({
              description: place,
              placeId: place,
            }))
          );
        } else if (response?.predictions) {
          // Handle Google Places API response format
          const validLocations = response.predictions
            .filter((place) => place?.description)
            .map((place) => ({
              description: place.description,
              placeId: place.place_id || place.description,
            }));
          setLocations(validLocations);
        } else {
          console.error("Invalid response format:", response);
          setLocations([]);
        }

        console.log(`Found ${locations.length} locations for query: ${input}`);
      }
    } catch (error) {
      console.error("Error fetching locations:", error);
      setLocations([]);
    }
  };

  const handleRemoveFile = () => {
    setInterviewData((prev) => ({ ...prev, attachment: null }));
  };

  const handleRemoveLink = () => {
    setInterviewData((prev) => ({ ...prev, assignment_link: "" }));
  };

  const handleDateChange = (newValue) => {
    const isoString = newValue ? newValue.toISOString() : "";
    handleChange({
      target: {
        name: "deadline",
        value: isoString,
      },
    });
  };

  const handleTimeChange = (name, value) => {
    if (!value) return;

    if (isBusyDate(value)) {
      const busySlots = getBusySlots(value);
      setSelectedDateBusySlots(busySlots);
      setSelectedDate(value);
      setShowBusySlotModal(true);
      return;
    }

    const isoString = value ? value.toISOString() : "";
    handleChange({ target: { name, value: isoString } });

    let newErrors = { ...errors };
    const start =
      name === "startTime" ? value : dayjs(interviewData?.startTime);
    const end = name === "endTime" ? value : dayjs(interviewData?.endTime);

    newErrors.startTime = "";
    newErrors.endTime = "";

    // Validations for startTime
    if (name === "startTime") {
      setCurrentMonth(value.startOf("month"));
      if (!value || value.isBefore(now)) {
        newErrors.startTime = "Start time cannot be in the past";
      } else if (value.isAfter(maxDate)) {
        newErrors.startTime = "Start time must be within 30 days from now";
      } else if (!value.isBefore(now.add(12, "hour"))) {
        newErrors.startTime = "Start time must be within the next 12 hours";
      }
    }

    // Validations for endTime
    if (start && end) {
      if (!start.isSame(end, "day")) {
        newErrors.endTime = "Start and end must be on the same day";
      } else if (end.isBefore(start)) {
        newErrors.endTime = "End time cannot be earlier than start time";
      }
    }

    const isoofString = value ? value.toISOString() : "";
    handleChange({ target: { name, value: isoofString } });

    setErrors(newErrors);
  };

  const handleMonthChange = (date) => {
    // Always use the 1st day of the *next* month
    const firstDayOfNextMonth = dayjs(date).add(1, "month").startOf("month");
    setCurrentMonth(firstDayOfNextMonth);

    const timeMin = firstDayOfNextMonth.format("YYYY-MM-DD"); // 👈 fixed
    const interviewerEmails = interviewData?.interviewers_list?.join(",");

    console.log(
      "Fetching availability for:",
      interviewerEmails,
      "from:",
      timeMin
    );

    if (!interviewerEmails) {
      console.warn("No interviewers selected yet.");
      return;
    }

    const params = {
      emails: interviewerEmails,
      timeMin: timeMin,
    };

    dispatch(getInterviwerEvents(params)).then((result) => {
      console.log("Interviewer availability result:", result);
      if (result.payload?.events) {
        const dates = result.payload.events.map((event) =>
          dayjs(event.start).format("MM-DD-YYYY")
        );
        setBusyDates(dates);
      }
    });
  };

  // const handleDateSelection = (date) => {
  //   if (!date) return;

  //   // If there's already a selected time, check the exact datetime
  //   if (parsedStart) {
  //     const newDateTime = date
  //       .hour(parsedStart.hour())
  //       .minute(parsedStart.minute());

  //     // Check if any interviewer is busy at this datetime
  //     const busySlots = getBusySlots(date);
  //     const isTimeSlotBusy = busySlots.some(slot => {
  //       const slotStart = dayjs(slot.start);
  //       const slotEnd = dayjs(slot.end);
  //       return newDateTime.isBetween(slotStart, slotEnd, 'minute', '[]');
  //     });

  //     if (isTimeSlotBusy) {
  //       setSelectedDateBusySlots(busySlots);
  //       setSelectedDate(newDateTime);
  //       // setShowBusySlotModal(true);
  //       return;
  //     }
  //   }

  //   // If no time selected yet, just update the date
  //   const currentTime = parsedStart ? parsedStart : dayjs();
  //   const newDateTime = date
  //     .hour(currentTime.hour())
  //     .minute(currentTime.minute());

  //   handleChange({
  //     target: {
  //       name: "startTime",
  //       value: newDateTime.toISOString(),
  //     },
  //   });
  // };

  // const handleTimeSelection = (field, time) => {
  //   if (!time || !parsedStart) return;

  //   // Create new datetime by combining selected date with new time
  //   const newDateTime = parsedStart
  //     .hour(time.hour())
  //     .minute(time.minute());

  //   // Get busy slots for the selected date
  //   const busySlots = getBusySlots(parsedStart);

  //   // Check if the selected time conflicts with any busy slots
  //   const isTimeSlotBusy = busySlots.some(slot => {
  //     const slotStart = dayjs(slot.start);
  //     const slotEnd = dayjs(slot.end);
  //     return newDateTime.isBetween(slotStart, slotEnd, 'minute', '[]');
  //   });

  //   if (isTimeSlotBusy) {
  //     const relevantBusySlots = busySlots.filter(slot => {
  //       const slotStart = dayjs(slot.start);
  //       const slotEnd = dayjs(slot.end);
  //       return newDateTime.isBetween(slotStart, slotEnd, 'minute', '[]');
  //     });

  //     setSelectedDateBusySlots(relevantBusySlots);
  //     setSelectedDate(newDateTime);
  //     setShowBusySlotModal(true);
  //     return;
  //   }

  //   handleChange({
  //     target: {
  //       name: field,
  //       value: newDateTime.toISOString(),
  //     },
  //   });
  // };
  // const handleDateSelection = (date) => {
  //   if (!date) return;

  //   const now = dayjs();
  //   const selectedDateStart = date.startOf('day');
  //   const todayStart = now.startOf('day');

  //   // If selecting today's date, ensure time is in future
  //   if (selectedDateStart.isSame(todayStart, 'day')) {
  //     // If no time is selected yet or selected time is in past, set to current time + 1 hour
  //     let newDateTime;
  //     if (!parsedStart || parsedStart.isBefore(now)) {
  //       newDateTime = now.add(1, 'hour').minute(0); // Round to next hour
  //     } else {
  //       // Keep the selected time but ensure it's today
  //       newDateTime = date
  //         .hour(parsedStart.hour())
  //         .minute(parsedStart.minute());

  //       // If the combined datetime is in past, adjust to current time + 1 hour
  //       if (newDateTime.isBefore(now)) {
  //         newDateTime = now.add(1, 'hour').minute(0);
  //       }
  //     }


  //     handleChange({
  //       target: { name: "startTime", value: newDateTime.toISOString() },
  //     });
  //   } else {
  //     // For future dates, keep existing time or set to reasonable default (10 AM)
  //     const currentTime = parsedStart ? parsedStart : date.hour(10).minute(0); // Default to 10 AM
  //     const newDateTime = date
  //       .hour(currentTime.hour())
  //       .minute(currentTime.minute());

  //     handleChange({
  //       target: { name: "startTime", value: newDateTime.toISOString() },
  //     });
  //   }
  // };

  const handleDateSelection = (date) => {
    if (!date) return;

    const now = dayjs();
    const selectedDateStart = date.startOf("day");
    const todayStart = now.startOf("day");

    let newStartDateTime;
    let newEndDateTime;

    if (selectedDateStart.isSame(todayStart, "day")) {
      // ensure time is in future (use now + 1h)
      const nextHour = now.add(1, "hour");
      const roundedMinutes = Math.ceil(nextHour.minute() / 30) * 30;
      newStartDateTime = nextHour.minute(roundedMinutes % 60).second(0);
      newEndDateTime = newStartDateTime.add(1, "hour");
    } else {
      newStartDateTime = date.hour(10).minute(0).second(0);
      newEndDateTime = date.hour(11).minute(0).second(0);
    }

    setInterviewData((prev) => ({
      ...prev,
      startTime: newStartDateTime.toISOString(),
      endTime: newEndDateTime.toISOString(),
    }));

    // Validate both times so UI shows proper error messages
    validateTimes(newStartDateTime, newEndDateTime);
  };




  useEffect(() => {
    if (parsedStart && parsedStart.isAfter(dayjs())) {
      setErrors(prev => ({
        ...prev,
        startTime: "",
        endTime: ""
      }));
    }
  }, [parsedStart]);

  // const handleTimeSelection = (field, time) => {
  //   if (!time || !parsedStart) return;

  //   // Create new datetime by combining selected date with new time
  //   const newDateTime = parsedStart.hour(time.hour()).minute(time.minute());

  //   // Check if the selected time is in the past
  //   if (newDateTime.isBefore(dayjs())) {
  //     setErrors(prev => ({
  //       ...prev,
  //       [field]: "Cannot schedule interview in the past"
  //     }));
  //     return;
  //   }

  //   // Get all busy slots for the selected date
  //   const busySlots = getBusySlots(parsedStart);

  //   // Check if the selected time conflicts with any slots
  //   const conflictingSlots = busySlots.filter((slot) => {
  //     const slotStart = dayjs(slot.start);
  //     const slotEnd = dayjs(slot.end);
  //     return newDateTime.isBetween(slotStart, slotEnd, 'minute', '[]');
  //   });

  //   if (conflictingSlots.length > 0) {
  //     setSelectedDateBusySlots(conflictingSlots);
  //     setSelectedDate(newDateTime);
  //     setShowBusySlotModal(true);
  //   } else {
  //     // Clear any existing time-related errors
  //     setErrors(prev => ({
  //       ...prev,
  //       [field]: ""
  //     }));

  //     // Proceed to update the time if no conflicts
  //     handleChange({
  //       target: { name: field, value: newDateTime.toISOString() },
  //     });
  //   }
  // };

  // const handleTimeSelection = (field, time) => {
  //   if (!time || !parsedStart) return;

  //   // Use the parsedStart date with the selected time's hour/minute to preserve the selected date
  //   const newDateTime = parsedStart
  //     .hour(time.hour())
  //     .minute(time.minute())
  //     .second(0)
  //     .millisecond(0);

  //   // Allow 1 minute early; otherwise it's in the past
  //   const currentTimeMinusOneMinute = dayjs().subtract(1, "minute");
  //   if (newDateTime.isBefore(currentTimeMinusOneMinute)) {
  //     setErrors((prev) => ({
  //       ...prev,
  //       [field]: "Cannot schedule interview more than 1 minute in the past",
  //     }));
  //     return;
  //   }

  //   // If the time conflicts with busy slots, show the busy modal
  //   const busySlots = getBusySlots(parsedStart);
  //   const conflictingSlots = busySlots.filter((slot) => {
  //     const slotStart = dayjs(slot.start);
  //     const slotEnd = dayjs(slot.end);
  //     return newDateTime.isBetween(slotStart, slotEnd, "minute", "[]");
  //   });

  //   if (conflictingSlots?.length > 0) {
  //     setSelectedDateBusySlots(conflictingSlots);
  //     setSelectedDate(newDateTime);
  //     setShowBusySlotModal(true);
  //     return;
  //   }

  //   // Update state in a single setInterviewData call to keep UI consistent
  //   setInterviewData((prev) => {
  //     const updated = {
  //       ...prev,
  //       [field]: newDateTime.toISOString(),
  //     };

  //     if (field === "startTime") {
  //       // If there's no endTime or endTime is not after startTime, default to start+1 hour
  //       const currentEnd = prev?.endTime ? dayjs(prev.endTime) : null;
  //       if (!currentEnd || !currentEnd.isAfter(newDateTime)) {
  //         updated.endTime = newDateTime.add(1, "hour").toISOString();
  //       }
  //       // clear errors if any
  //       setErrors((prevErr) => ({ ...prevErr, startTime: "", endTime: "" }));
  //     } else if (field === "endTime") {
  //       // Ensure end is after start
  //       const start = prev?.startTime ? dayjs(prev.startTime) : null;
  //       if (start && !newDateTime.isAfter(start)) {
  //         setErrors((prevErr) => ({
  //           ...prevErr,
  //           endTime: "End time must be greater than start time",
  //         }));
  //       } else {
  //         setErrors((prevErr) => ({ ...prevErr, endTime: "" }));
  //       }
  //     }

  //     return updated;
  //   });
  // };

  const handleTimeSelection = (field, time) => {
    if (!time || !parsedStart) return;

    const newDateTime = parsedStart
      .hour(time.hour())
      .minute(time.minute())
      .second(0)
      .millisecond(0);

    // Basic past check
    if (newDateTime.isBefore(dayjs())) {
      setErrors((prev) => ({
        ...prev,
        [field]: field === "startTime" ? "Start time cannot be in the past" : "End time cannot be in the past",
      }));
      return;
    }

    // If the time conflicts with busy slots, show the busy modal
    const busySlots = getBusySlots(parsedStart);
    const conflictingSlots = busySlots.filter((slot) => {
      const slotStart = dayjs(slot.start);
      const slotEnd = dayjs(slot.end);
      return newDateTime.isBetween(slotStart, slotEnd, "minute", "[]");
    });

    if (conflictingSlots?.length > 0) {
      setSelectedDateBusySlots(conflictingSlots);
      setSelectedDate(newDateTime);
      setShowBusySlotModal(true);
      return;
    }

    setInterviewData((prev) => {
      const existingStart = prev.startTime ? dayjs(prev.startTime) : null;
      const existingEnd = prev.endTime ? dayjs(prev.endTime) : null;

      const resultingStart = field === "startTime" ? newDateTime : existingStart;
      let resultingEnd = field === "endTime" ? newDateTime : existingEnd;

      // If user changes start and end is missing/not > start, default end to start+1h
      if (field === "startTime") {
        if (!resultingEnd || !resultingEnd.isAfter(resultingStart)) {
          resultingEnd = resultingStart.add(1, "hour");
        }
      }

      const updated = {
        ...prev,
        [field]: newDateTime.toISOString(),
        ...(field === "startTime" && { endTime: resultingEnd.toISOString() }),
      };

      // run validation with final computed values
      validateTimes(resultingStart, resultingEnd);

      return updated;
    });
  };

  // Separate function to validate end time
  const validateEndTime = (startTime, endTime) => {
    if (!startTime || !endTime) return;

    if (!endTime.isAfter(startTime)) {
      setErrors(prev => ({
        ...prev,
        endTime: "End time must be greater than start time"
      }));
    } else {
      setErrors(prev => ({
        ...prev,
        endTime: ""
      }));
    }
  };
  const getBusyInterviewers = (date) => {
    if (!interviewersbusyslots || !date) return [];

    const dateStr = dayjs(date).format("YYYY-MM-DD");
    const busyInterviewers = [];

    Object.entries(interviewersbusyslots).forEach(([email, slots]) => {
      if (
        slots.some((slot) => dayjs(slot.start).format("YYYY-MM-DD") === dateStr)
      ) {
        const interviewer = interviewers.find((i) => i.email === email);
        if (interviewer) {
          busyInterviewers.push({
            name: `${interviewer.first_name} ${interviewer.last_name}`,
            email,
            avatar: interviewer.profile_url,
          });
        }
      }
    });

    return busyInterviewers;
  };



  const interviewRoundTitles = [
    "HR Interview",
    "Technical Interview",
    "Coding Round",
    "System Design Interview",
    "Behavioral Interview",
    "Managerial Round",
    "Final Interview",
  ];

  const BusyDotIndicator = () => (
    <Box
      sx={{
        position: "absolute",
        top: 2,
        right: 2,
        width: 6,
        height: 6,
        borderRadius: "50%",
        backgroundColor: "error.main",
      }}
    />
  );

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{ "& .MuiDrawer-paper": { width: "400px", padding: "20px" } }}
    >
      <Box>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
          }}
        >
          <Typography variant="h6" fontWeight="bold" color="#0044C6">
            {existingRound ? "Reschedule Interview" : "Schedule Interview"}
          </Typography>
          <IconButton onClick={onClose} sx={{ color: "#0044C6" }}>
            <CloseIcon />
          </IconButton>
        </Box>

        <FormControl fullWidth sx={{ mb: 2 }}>
          <Autocomplete
            freeSolo
            options={interviewRoundTitles}
            value={interviewData?.title || ""}
            onChange={(event, newValue) => {
              handleChange({
                target: { name: "title", value: newValue },
              });
            }}
            onInputChange={(event, newInputValue) => {
              handleChange({
                target: { name: "title", value: newInputValue },
              });
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Round Title*"
                error={!!errors?.title}
                helperText={errors?.title}
                fullWidth
              />
            )}
          />

          <Select
            name="type"
            value={interviewData?.type}
            onChange={handleChange}
            sx={{ mt: 2 }}
            error={!!errors?.type}
            displayEmpty
          >
            <MenuItem value="" disabled>
              Select Interview Type*
            </MenuItem>
            <MenuItem value="F2F">Face-to-Face</MenuItem>
            <MenuItem value="Remote">Remote</MenuItem>
            <MenuItem value="Assignment">Assignment</MenuItem>
          </Select>
        </FormControl>

        {/* {interviewData?.type === "F2F" && (
          <Autocomplete
            freeSolo
            options={locations}
            getOptionLabel={(option) => {
              if (!option) return "";
              return typeof option === "string"
                ? option
                : option.description || "";
            }}
            value={interviewData?.interview_location || null}
            onChange={(event, newValue) => {
              // Create a synthetic event object
              handleChange({
                target: {
                  name: "interview_location",
                  value:
                    typeof newValue === "string"
                      ? newValue
                      : newValue?.description || "",
                },
                // Add preventDefault method
                preventDefault: () => { },
              });
            }}
            onInputChange={(event, newValue) => {
              // Only call onChangeLocation if it's a real change event
              if (event?.type === "change") {
                onChangeLocation(newValue);
              }
            }}
            filterOptions={(options, params) => {
              const filtered = options?.filter((option) => {
                const value =
                  typeof option === "string" ? option : option.description;
                return value
                  .toLowerCase()
                  .includes(params.inputValue.toLowerCase());
              });
              return filtered;
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Job Location"
                required
                variant="outlined"
                fullWidth
                sx={{ mb: 2 }}
                // error={!!fieldErrors?.location}
                // helperText={fieldErrors?.location || ''}
                InputProps={{
                  ...params.InputProps,
                  style: { fontSize: "0.875rem" },
                }}
              />
            )}
            renderOption={(props, option) => (
              <li {...props}>
                <Typography variant="body2">
                  {typeof option === "string" ? option : option.description}
                </Typography>
              </li>
            )}
            noOptionsText="No locations found"
            loading={locations.length === 0}
            loadingText="Searching locations..."
          />
        )} */}

        {interviewData?.type === "F2F" && (
          <Autocomplete
            freeSolo
            options={locations}
            getOptionLabel={(option) => {
              if (!option) return "";
              if (typeof option === "string") return option;
              return option.description || "";
            }}
            value={interviewData?.interview_location || null}
            onChange={(event, newValue) => {
              // Handle selection from dropdown
              const locationValue = typeof newValue === "string"
                ? newValue
                : newValue?.description || "";

              setInterviewData(prev => ({
                ...prev,
                interview_location: locationValue
              }));

              // Clear error when location is provided
              if (locationValue) {
                setErrors(prev => ({ ...prev, interview_location: "" }));
              }
            }}
            onInputChange={(event, newInputValue) => {
              // Handle manual typing
              if (event && event.type === "change") {
                setInterviewData(prev => ({
                  ...prev,
                  interview_location: newInputValue
                }));

                // Clear error when user types something
                if (newInputValue.trim()) {
                  setErrors(prev => ({ ...prev, interview_location: "" }));
                }

                // Fetch suggestions when typing
                if (newInputValue.length > 2) {
                  onChangeLocation(newInputValue);
                }
              }
            }}
            filterOptions={(options, params) => {
              const filtered = options?.filter((option) => {
                const value = typeof option === "string" ? option : option.description;
                return value
                  .toLowerCase()
                  .includes(params.inputValue.toLowerCase());
              });
              return filtered;
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Job Location*"
                required
                variant="outlined"
                fullWidth
                sx={{ mb: 2 }}
                error={!!errors?.interview_location}
                helperText={errors?.interview_location}
                InputProps={{
                  ...params.InputProps,
                  style: { fontSize: "0.875rem" },
                }}
              />
            )}
            renderOption={(props, option) => (
              <li {...props}>
                <Typography variant="body2">
                  {typeof option === "string" ? option : option.description}
                </Typography>
              </li>
            )}
            noOptionsText="Type a location or select from suggestions"
            loading={locations.length === 0}
            loadingText="Searching locations..."
          />
        )}

        <TextField
          name="description"
          label="Description*"
          multiline
          rows={3}
          value={interviewData?.description}
          onChange={handleChange}
          error={!!errors?.description}
          helperText={errors?.description}
          fullWidth
          sx={{ mb: 2 }}
        />

        <Button
          variant="outlined"
          fullWidth
          sx={{
            mb: 2,
            borderColor: "#0044C6",
            color: "#0044C6",
            "&:hover": {
              backgroundColor: "#F0F5FF",
              borderColor: "#003399",
            },
          }}
          onClick={() => {
            setInterviewData((prev) => ({
              ...prev,
              description: `Dear ${applicant?.name}, your interview for the position of ${applicant?.role} has been scheduled. Please be prepared and ensure your availability at the scheduled date and time. We look forward to speaking with you.`,
            }));

            // Clear any existing description errors
            setErrors((prev) => ({ ...prev, description: "" }));
          }}
          startIcon={
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path
                d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm2 14h-4v-4h4v4zm0-5.5V12h-4V9h4v1.5z"
                fill="currentColor"
              />
            </svg>
          }
        >
          <Box component="span" sx={{ fontWeight: 600 }}>
            Generate with AI
          </Box>
        </Button>
        {(interviewData?.type === "F2F" ||
          interviewData?.type === "Remote") && (
            <>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <Typography sx={{ color: "#0044C6" }}>
                  Interviewers ({interviewData?.interviewers_list?.length}/
                  {maxInterviewers})
                </Typography>
                <IconButton
                  onClick={() => setInterviewerModalOpen(true)}
                  sx={{
                    color: "#0044C6",
                    "&.Mui-disabled": {
                      color: "rgba(0, 68, 198, 0.3)",
                    },
                  }}
                  disabled={
                    interviewData?.interviewers_list?.length >= maxInterviewers
                  }
                >
                  <AddIcon />
                </IconButton>
              </Box>

              <Box sx={{ mb: 2 }}>
                {interviewData?.interviewers_list?.map((email, index) => {
                  const interviewer = getInterviewerDetails(email);
                  return (
                    <Box
                      key={index}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        mb: 1,
                        p: 1,
                        bgcolor: "#f5f5f5",
                        borderRadius: 1,
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <Box
                          component="img"
                          src={
                            interviewer?.profile_url ||
                            "https://static.vecteezy.com/system/resources/thumbnails/009/734/564/small_2x/default-avatar-profile-icon-of-social-media-user-vector.jpg"
                          }
                          alt={interviewer?.first_name || "Avatar"}
                          sx={{
                            width: 30,
                            height: 30,
                            borderRadius: "50%",
                            mr: 1,
                          }}
                        />
                        <Box sx={{ display: "flex", flexDirection: "column" }}>
                          <Typography sx={{ color: "#0044C6" }}>
                            {interviewer
                              ? `${interviewer?.first_name} ${interviewer?.last_name}`
                              : "Unknown User"}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ color: "text.secondary" }}
                          >
                            {email}
                          </Typography>
                        </Box>
                      </Box>
                      <IconButton
                        size="small"
                        onClick={() => removeInterviewer(email)}
                        sx={{ color: "error.main" }}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  );
                })}
                {errors?.interviewers && (
                  <Typography color="error" variant="caption">
                    {errors?.interviewers}
                  </Typography>
                )}
              </Box>
            </>
          )}

        {interviewData?.type === "Remote" && (
          <TextField
            name="meet_link"
            label="meet_link*"
            value={interviewData?.meet_link}
            onChange={handleChange}
            error={!!errors?.meet_link}
            helperText={errors?.meet_link}
            fullWidth
            sx={{ mb: 2 }}
          />
        )}

        {(interviewData?.type === "F2F" ||
          interviewData?.type === "Remote") && (
            <>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={12}>
                    <FormControl fullWidth>
                      <DatePicker
                        label="Select Date*"
                        value={parsedStart}
                        onChange={(date) => handleDateSelection(date)}
                        minDate={now}
                        maxDate={maxDate}
                        onMonthChange={handleMonthChange}
                        slotProps={{
                          textField: {
                            InputProps: { readOnly: true },
                          },
                        }}
                        renderDay={(day, selected, dayProps) => {
                          const isBusy = isBusyDate(day);
                          return (
                            <Box sx={{ position: "relative" }}>
                              <PickersDay {...dayProps} selected={selected} />
                              {isBusy && <BusyDotIndicator />}
                            </Box>
                          );
                        }}
                      />
                    </FormControl>
                  </Grid>

                  {parsedStart && (
                    <>
                      <Grid item xs={12} md={6}>
                        <FormControl fullWidth error={!!errors?.startTime}>
                          {/* <TimePicker
                          label="Start Time*"
                          value={parsedStart}
                          onChange={(time) =>
                            handleTimeSelection("startTime", time)
                          }
                          minutesStep={1}
                          disabled={!parsedStart}
                          minTime={parsedStart}
                          maxTime={parsedStart?.endOf("day")}
                          slotProps={{
                            textField: {
                              InputProps: { readOnly: true },
                            },
                          }}
                          renderOption={(props, option) => {
                            const time = dayjs(option);
                            const busySlots = getBusySlots(parsedStart);
                            const isBusy = busySlots.some((slot) =>
                              time.isBetween(
                                dayjs(slot.start),
                                dayjs(slot.end),
                                "minute",
                                "[]"
                              )
                            );

                            return (
                              <MenuItem
                                {...props}
                                sx={{
                                  position: "relative",
                                  ...(isBusy && {
                                    color: "error.main",
                                    "&::after": {
                                      content: '"•"',
                                      position: "absolute",
                                      right: 8,
                                      color: "error.main",
                                    },
                                  }),
                                }}
                              >
                                {time.format("hh:mm A")}
                              </MenuItem>
                            );
                          }}
                        /> */}
                          {/* <TimePicker
                            label="Start Time*"
                            value={parsedStart}
                            onChange={(time) => handleTimeSelection("startTime", time)}
                            minutesStep={1} // Use 15-minute intervals for better UX
                            disabled={!parsedStart}
                            minTime={parsedStart?.isSame(dayjs(), 'day') ? dayjs() : parsedStart?.startOf('day')}
                            maxTime={parsedStart?.endOf("day")}
                            slotProps={{
                              textField: {
                                InputProps: { readOnly: true },
                              },
                            }}
                            views={['hours', 'minutes']}
                            ampm={true} // Ensure AM/PM is displayed
                            format="hh:mm A" // Explicit format for AM/PM
                          /> */}
                          <TimePicker
                            label="Start Time*"
                            value={parsedStart}
                            onChange={(time) => handleTimeSelection("startTime", time)}
                            error={!!errors?.startTime}
                            helperText={errors?.startTime}
                            disabled={!parsedStart}
                            minTime={dayjs()} // Current time for start time
                            maxTime={parsedStart?.endOf("day")}
                          />
                          {!!errors?.startTime && (
                            <FormHelperText>{errors?.startTime}</FormHelperText>
                          )}
                        </FormControl>
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <FormControl fullWidth error={!!errors?.endTime}>
                          {/* <TimePicker
                            label="End Time*"
                            value={
                              interviewData?.endTime
                                ? dayjs(interviewData?.endTime)
                                : null
                            }
                            onChange={(time) =>
                              handleTimeSelection("endTime", time)
                            }
                            minutesStep={1}
                            disabled={!parsedStart}
                            minTime={parsedStart}
                            maxTime={parsedStart?.endOf("day")}
                            slotProps={{
                              textField: {
                                InputProps: { readOnly: true },
                              },
                            }}
                          /> */}
                          <TimePicker
                            label="End Time*"
                            value={interviewData?.endTime ? dayjs(interviewData?.endTime) : null}
                            onChange={(time) => handleTimeSelection("endTime", time)}
                            error={!!errors?.endTime}
                            helperText={errors?.endTime}
                            disabled={!parsedStart}
                            minTime={parsedStart} // Start time for end time (can't end before start)
                            maxTime={parsedStart?.endOf("day")}
                          />
                          {!!errors?.endTime && (
                            <FormHelperText>{errors?.endTime}</FormHelperText>
                          )}
                        </FormControl>
                      </Grid>
                    </>
                  )}
                </Grid>
              </LocalizationProvider>
            </>
          )}
        {interviewData?.type === "Assignment" && (
          <>
            {/* File Upload Section */}
            {!interviewData?.assignment_link && (
              <div>
                {interviewData?.attachment ? (
                  // Display uploaded file with remove button
                  <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                    <Typography variant="body2">
                      Uploaded: {interviewData?.attachment?.name}
                    </Typography>
                    <IconButton
                      onClick={handleRemoveFile}
                      size="small"
                      sx={{ ml: 1 }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ) : (
                  // Upload button and accepts text
                  <>
                    <Button
                      variant="contained"
                      component="label"
                      fullWidth
                      sx={{ mb: 2 }}
                    >
                      <UploadFileIcon /> Upload File
                      <input
                        type="file"
                        hidden
                        onChange={handleFileUpload}
                        accept=".zip,.pdf,.doc,.docx,.png,.jpg"
                      />
                    </Button>
                    <Typography sx={{ fontSize: "12px", mb: "10px" }}>
                      Accepts: zip, pdf, doc, docx, png, jpg
                    </Typography>
                  </>
                )}
              </div>
            )}

            {/* OR Text - only show when neither is present */}
            {!interviewData?.assignment_link && !interviewData?.attachment && (
              <Typography sx={{ textAlign: "center", my: 2 }}>OR</Typography>
            )}

            {/* Assignment Link Section */}
            {!interviewData?.attachment && (
              <div>
                {interviewData?.assignment_link ? (
                  // Display link with remove button
                  <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                    <Typography variant="body2">
                      Link: {interviewData?.assignment_link}
                    </Typography>
                    <IconButton
                      onClick={handleRemoveLink}
                      size="small"
                      sx={{ ml: 1 }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ) : (
                  // Link input field
                  <TextField
                    name="assignment_link"
                    label="Assignment link"
                    type="text"
                    value={interviewData?.assignment_link || ""}
                    onChange={handleChange}
                    error={!!errors?.assignment_link}
                    helperText={errors?.assignment_link}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                    sx={{ mb: 2 }}
                  />
                )}
              </div>
            )}
            {/* <TextField
      name="deadline"
      label="Submission Deadline*"
      type="datetime-local"
      value={interviewData?.deadline}
      onChange={handleChange}
      error={!!errors?.deadline}
      helperText={errors?.deadline}
      InputLabelProps={{ shrink: true }}
      fullWidth
      sx={{ mb: 2 }}
    /> */}
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <FormControl fullWidth sx={{ mb: 2 }} error={!!errors?.deadline}>
                <DateTimePicker
                  label="Submission Deadline*"
                  value={
                    interviewData?.deadline
                      ? dayjs(interviewData.deadline)
                      : null
                  }
                  onChange={handleDateChange}
                  minDateTime={dayjs()} // Now is the earliest allowed
                  maxDateTime={dayjs().add(30, "day")} // 30 days from now
                  disablePast
                  slotProps={{
                    textField: {
                      InputProps: { readOnly: true },
                      fullWidth: true,
                      onKeyDown: (e) => {
                        // Specifically prevent Enter and Backspace
                        if (e.key === 'Enter' || e.key === 'Backspace' || e.key === 'Delete') {
                          e.preventDefault();
                          return false;
                        }
                      },
                    },
                  }}
                />
                {!!errors?.deadline && (
                  <FormHelperText>{errors.deadline}</FormHelperText>
                )}
              </FormControl>
            </LocalizationProvider>
          </>
        )}

        <Box sx={{ mt: 3, display: "flex", gap: 2 }}>
          <Button
            onClick={onClose}
            variant="outlined"
            fullWidth
            sx={{ color: "gray", borderColor: "gray" }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            fullWidth
            disabled={isSubmitting}
            sx={{ bgcolor: "#0044C6", "&:hover": { bgcolor: "#003399" } }}
          >
            {isSubmitting ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              existingRound ? "ReSchedule" : "Schedule"
            )}
          </Button>
        </Box>
      </Box>

      <Dialog
        open={isInterviewerModalOpen}
        onClose={() => setInterviewerModalOpen(false)}
        fullWidth
      >
        <DialogTitle>Add Interviewer</DialogTitle>
        <DialogContent>
          {/* <TextField
            name="newInterviewer"
            label="Email"
            fullWidth
            value={newInterviewer}
            onChange={(e) => setNewInterviewer(e.target.value)}
            error={!!errors?.newInterviewer}
            helperText={errors?.newInterviewer}
          /> */}
          {interviewers && interviewers?.length > 0 ? (
            <Box
              sx={{
                mb: 2,
                maxHeight: 300,
                overflowY: "scroll",
                scrollbarWidth: "thin",
              }}
            >
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Select Interviewers (Max {maxInterviewers})
              </Typography>
              {interviewers?.map((interviewer) => (
                <Box
                  key={interviewer?.email}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    mb: 1,
                    p: 1,
                    border: "1px solid #eee",

                    borderRadius: 1,
                  }}
                >
                  <Box
                    component="label"
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      flex: 1,
                      cursor: "pointer",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={selectedInterviewers?.some(
                        (i) => i.email === interviewer?.email
                      )}
                      onChange={() => handleInterviewerSelect(interviewer)}
                      disabled={
                        selectedInterviewers?.length >= maxInterviewers &&
                        !selectedInterviewers?.some(
                          (i) => i.email === interviewer?.email
                        )
                      }
                    />
                    <Box sx={{ display: "flex", alignItems: "center", ml: 1 }}>
                      <Box
                        component="img"
                        src={
                          interviewer?.profile_url ||
                          "https://static.vecteezy.com/system/resources/thumbnails/009/734/564/small_2x/default-avatar-profile-icon-of-social-media-user-vector.jpg"
                        }
                        alt={interviewer?.first_name}
                        sx={{
                          width: 30,
                          height: 30,
                          borderRadius: "50%",
                          mr: 1,
                        }}
                      />
                      <Box sx={{ display: "flex", flexDirection: "column" }}>
                        <Typography>
                          {`${interviewer?.first_name} ${interviewer?.last_name}` ||
                            "user"}
                        </Typography>
                        <Typography>{`${interviewer?.email}`}</Typography>
                      </Box>
                    </Box>
                  </Box>
                </Box>
              ))}
            </Box>
          ) : (
            <Typography color="text.secondary" sx={{ mb: 2 }}>
              No interviewers found. Please add manually.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInterviewerModalOpen(false)}>Cancel</Button>
          {interviewers &&
            interviewers?.length > 0 &&
            selectedInterviewers?.length > 0 ? (
            <Button onClick={handleAddSelectedInterviewers} variant="contained">
              Add Selected ({selectedInterviewers?.length})
            </Button>
          ) : (
            <Button onClick={handleAddInterviewer} variant="contained">
              Add Email
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <BusySlotModal
        open={showBusySlotModal}
        onClose={() => setShowBusySlotModal(false)}
        slots={selectedDateBusySlots}
        date={selectedDate}
        onScheduleAnyway={() => {
          // Set start and end when scheduling anyway. Use selectedDate + 1 hour for default end
          if (!selectedDate) return;
          setInterviewData(prev => ({
            ...prev,
            startTime: selectedDate.toISOString(),
            endTime: selectedDate.add(1, 'hour').toISOString()
          }));
          setShowBusySlotModal(false);
        }}
      />
    </Drawer>
  );
};

export default InterviewScheduleSlider;
